import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionableNotification,
  RadioTile,
  Search,
  SkeletonText,
  TileGroup,
} from '@carbon/react';
import { parseUTCdateTime } from 'utils/dates';
import { DrillInHeader } from './drill-in-header';
import { usePriceDecks } from './hooks';
import styles from './scenario-picker-drill-in.module.scss';

/** Above this many decks the client-side search field appears, as in the scenario picker. */
const SEARCH_THRESHOLD = 6;

export interface PriceDeckPickerDrillInProps {
  /**
   * The case's own deck: forced into the list even if archived, and marked as the case's.
   * `undefined` while the case's detail loads: no deck can be picked until it is known, or a pick
   * of the case's own deck would be stored as this run's.
   */
  caseDeckId: number | null | undefined;
  /**
   * The deck this run uses now (the per-run pick, else the case's): checked on open, and again
   * whenever it changes (the case's detail arriving names the case's deck).
   */
  currentDeckId: number | null;
  /** The case's detail failed to load, so `caseDeckId` won't arrive: offer Retry. */
  caseDetailFailed?: boolean;
  /** Reloads the case's detail. */
  onRetryCaseDetail?: () => void;
  /** Commit a deck for this run; the case's own deck clears the pick. */
  onPick: (deckId: number) => void;
  /** Return to the main panel view. */
  onBack: () => void;
}

/**
 * The Price Deck row's picker: the active decks (the list the Price Deck dropdown used, with the
 * case's own deck forced in), as RadioTiles like the scenario picker's, with the run's deck
 * checked and scrolled into view. Picking commits at once and keeps the picker open, so the check
 * visibly lands; Back leaves.
 *
 * Picking the case's own deck (marked "The case's deck") puts the run back on it, the way
 * re-picking the case's scenario resets a scenario row.
 *
 * @param {PriceDeckPickerDrillInProps} props - The case's and the run's decks, and the handlers.
 * @return {JSX.Element} The deck picker drill-in.
 */
export function PriceDeckPickerDrillIn({
  caseDeckId,
  currentDeckId,
  caseDetailFailed = false,
  onRetryCaseDetail,
  onPick,
  onBack,
}: PriceDeckPickerDrillInProps) {
  // Keyed on the case's deck, as Case management's prefetch is, so opening this is a cache hit.
  const decks = usePriceDecks(caseDeckId ?? null, {
    enabled: caseDeckId !== undefined,
  });
  // Nothing to pick until the case's deck is known, even from a list another key cached.
  const waiting = caseDeckId === undefined;
  // A case detail that failed won't arrive on its own: say so, with Retry, not a skeleton forever.
  const detailFailed = waiting && caseDetailFailed;
  const isLoading = waiting ? !detailFailed : decks.isPending;
  const isError = !waiting && decks.isError;
  const { data, refetch } = decks;

  const [query, setQuery] = useState('');
  // The checked deck is the run's deck as it stands: every pick commits at once (onPick), and it
  // follows the case's deck as that becomes known, with no local copy to fall out of step.
  const selectedId = currentDeckId;
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const options = useMemo(() => (waiting ? [] : (data ?? [])), [waiting, data]);
  const showSearch = options.length > SEARCH_THRESHOLD;
  const term = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!term) {
      return options;
    }
    return options.filter((deck) => deck.name.toLowerCase().includes(term));
  }, [options, term]);

  // Once, bring the checked deck into view under the pinned header (see the scenario picker).
  useEffect(() => {
    if (hasScrolledRef.current || selectedId === null || options.length === 0) {
      return;
    }
    const input = rootRef.current?.querySelector<HTMLElement>(
      `#eval3-price-deck-tile-${selectedId}`
    );
    if (!input) {
      return;
    }
    hasScrolledRef.current = true;
    const tile = input.parentElement ?? input;
    tile.style.scrollMarginTop = `${headerRef.current?.offsetHeight ?? 0}px`;
    tile.scrollIntoView({ block: 'nearest' });
  }, [options, selectedId]);

  return (
    <div className={styles.picker} ref={rootRef}>
      <div className={styles.stickyHeader} ref={headerRef}>
        <DrillInHeader
          eyebrow="This run · Select price deck"
          title="Price Deck"
          onBack={onBack}
        />

        {showSearch && (
          <Search
            id="eval3-price-deck-search"
            size="sm"
            labelText="Search price decks"
            placeholder="Search price decks"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
      </div>

      {isLoading && <SkeletonText paragraph lineCount={5} />}

      {detailFailed && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the case"
          subtitle="The case's price deck is needed before a deck can be picked."
          actionButtonLabel="Retry"
          onActionButtonClick={() => onRetryCaseDetail?.()}
        />
      )}

      {isError && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load price decks"
          subtitle="The price-deck list is unavailable right now."
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {!isLoading && !isError && !detailFailed && filtered.length === 0 && (
        <p className={styles.emptyText}>
          {term
            ? `No price decks match "${query.trim()}".`
            : 'No active price decks.'}
        </p>
      )}

      {filtered.length > 0 && (
        <TileGroup
          className={styles.tileGroup}
          name="eval3-price-deck-picker"
          legend="Available price decks"
          valueSelected={selectedId !== null ? String(selectedId) : undefined}
          // TileGroup drops a re-select of the checked tile, so a no-op click never dirties the
          // form (the scenario picker relies on the same).
          onChange={(value: string | number) => onPick(Number(value))}
        >
          {filtered.map((deck) => (
            <RadioTile
              key={deck.id}
              id={`eval3-price-deck-tile-${deck.id}`}
              value={String(deck.id)}
            >
              <div className={styles.tileBody}>
                <span className={styles.tileName}>{deck.name}</span>
                {deck.id === caseDeckId && (
                  <span className={styles.tileMeta}>The case&rsquo;s deck</span>
                )}
                {deck.updatedAt && (
                  <span className={styles.tileMeta}>
                    Updated {parseUTCdateTime(deck.updatedAt)}
                  </span>
                )}
              </div>
            </RadioTile>
          ))}
        </TileGroup>
      )}
    </div>
  );
}
